import { z } from "zod";

/**
 * Which host the connect flow authorizes against. Mirrors `SalesforceEnvironment` in
 * `apps/api/src/salesforce/enums/salesforce.enum.ts`, and is deliberately **not** the login
 * host: `MY_DOMAIN` means "the host is in the `myDomain` parameter", which no fixed enum of
 * hosts can express.
 */
export enum SalesforceEnvironment {
	PRODUCTION = "PRODUCTION",
	SANDBOX = "SANDBOX",
	MY_DOMAIN = "MY_DOMAIN",
}

/** The two well-known login hosts, for display next to the picker's fixed options. */
export const SALESFORCE_PRODUCTION_HOST = "login.salesforce.com";
export const SALESFORCE_SANDBOX_HOST = "test.salesforce.com";

/**
 * A Salesforce My Domain host: `<name>.my.salesforce.com`, optionally with sandbox-style
 * infixes (`acme--uat.sandbox.my.salesforce.com`). Anchored at both ends, mirroring the
 * backend's `MY_DOMAIN_HOST_PATTERN` — an unanchored match would accept lookalikes such as
 * `foo.my.salesforce.com.attacker.net`.
 *
 * The backend is the authority (it re-validates before signing the OAuth state token); this
 * copy exists so the user sees the error in the field instead of after a round-trip.
 */
const MY_DOMAIN_HOST_PATTERN = /^[a-z0-9][a-z0-9-]*(\.[a-z0-9-]+)*\.my\.salesforce\.com$/;

/** The URL must be an origin only: https, no credentials, no port, no path/query/hash. */
const isBareHttpsOrigin = (parsed: URL): boolean =>
	parsed.protocol === "https:" &&
	parsed.username === "" &&
	parsed.password === "" &&
	parsed.port === "" &&
	(parsed.pathname === "" || parsed.pathname === "/") &&
	parsed.search === "" &&
	parsed.hash === "";

const parseUrl = (value: string): URL | null => {
	try {
		return new URL(value);
	} catch {
		return null;
	}
};

/**
 * Validate and normalise a customer-supplied My Domain URL exactly the way the backend's
 * `normalizeMyDomainOrigin` does. Returns the bare `https://<host>` origin, or `null` when
 * the value is not a My Domain URL.
 */
export const normalizeSalesforceMyDomain = (raw: string | null | undefined): string | null => {
	if (typeof raw !== "string" || raw.trim().length === 0) {
		return null;
	}

	const parsed = parseUrl(raw.trim().replace(/\/+$/, ""));

	if (!parsed || !isBareHttpsOrigin(parsed) || !MY_DOMAIN_HOST_PATTERN.test(parsed.hostname)) {
		return null;
	}

	return `https://${parsed.hostname}`;
};

export const isValidSalesforceMyDomain = (raw: string | null | undefined): boolean =>
	normalizeSalesforceMyDomain(raw) !== null;

/**
 * Form schema of the connection tab's environment picker.
 *
 * `myDomain` is only required — and only validated — when the environment is `MY_DOMAIN`,
 * which is the whole reason this is a `superRefine` rather than a field-level rule: the same
 * form has a valid empty state for the two fixed hosts. The messages are injected so the
 * component supplies translated strings (web §10) while the rule stays here.
 */
export const createSalesforceConnectionSchema = (messages: { required: string; invalid: string }) =>
	z
		.object({
			environment: z.nativeEnum(SalesforceEnvironment),
			myDomain: z.string().trim().optional(),
		})
		.superRefine((values, ctx) => {
			if (values.environment !== SalesforceEnvironment.MY_DOMAIN) {
				return;
			}

			if (!values.myDomain) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["myDomain"],
					message: messages.required,
				});

				return;
			}

			if (!isValidSalesforceMyDomain(values.myDomain)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["myDomain"],
					message: messages.invalid,
				});
			}
		});

export type SalesforceConnectionFormValues = z.infer<
	ReturnType<typeof createSalesforceConnectionSchema>
>;
